import React from "react";
import { useHistory } from "react-router-dom";

import { ROUTESLIST } from "@/static/urls";
import { formatDate } from "@/utils/common";
import useEntitlements from "@/hooks/useEntitlements";
import { addToCart, buildTopUpCartItem, clearCart, TOP_UP_EXPIRY_NOTE } from "@/services/cart";

/**
 * Top-up packs for a member who has run their allowance down. A credit is not a
 * gate: at a zero balance the member is silently charged the base price, so this
 * is the only signal they get that the discount has stopped applying.
 * Non-members see nothing — the API rejects a top-up without a membership.
 */
const TopUpPanel = ({ entitlements }) => {
  const history = useHistory();

  if (!entitlements || !entitlements.isMember || entitlements.topUps.length === 0) {
    return null;
  }

  const { on_grace_period: onGracePeriod, ends_at: endsAt } = entitlements.membership;

  const buy = (pack) => {
    // A membership and a top-up cannot be bought in one request.
    clearCart();
    addToCart(buildTopUpCartItem(pack));
    history.push(ROUTESLIST.CHECKOUT);
  };

  return (
    <div className="top-up-wrapper">
      <h5 className="top-up-title">Need more credits?</h5>
      <ul className="top-up-list">
        {entitlements.topUps.map((pack) => {
          const isEmpty = (entitlements.credits[pack.creditType] || 0) === 0;
          const expiring = (entitlements.creditsExpiring || {})[pack.creditType] || 0;

          return (
            <li key={pack.id} className="top-up-item">
              <span className="top-up-pack">
                {pack.name} — ${pack.price}
                {expiring > 0 && entitlements.creditsExpireAt && (
                  <small className="top-up-expiry" data-testid={`top-up-expiry-${pack.id}`}>
                    {expiring} {expiring === 1 ? "expires" : "expire"}{" "}
                    {formatDate(entitlements.creditsExpireAt)}
                  </small>
                )}
              </span>
              <button
                type="button"
                data-testid={`top-up-${pack.id}`}
                className={`btn top-up-btn ${isEmpty ? "primary-btn" : "top-up-btn-secondary"}`}
                onClick={() => buy(pack)}
              >
                Top up
              </button>
            </li>
          );
        })}
      </ul>
      {onGracePeriod ? (
        <p className="top-up-note top-up-note-warning" data-testid="top-up-grace-note">
          Your plan ends {formatDate(endsAt)}. Credits bought now expire with it.
        </p>
      ) : (
        <p className="top-up-note">{TOP_UP_EXPIRY_NOTE}</p>
      )}
    </div>
  );
};


/**
 * The panel wired to the API, for the class-component plan page. Balances are
 * re-read on mount, which is where checkout lands the buyer back.
 */
export const TopUpPanelContainer = () => {
  const { entitlements, loading } = useEntitlements();

  if (loading) return null;

  return <TopUpPanel entitlements={entitlements} />;
};

export default TopUpPanel;
